import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Briefcase, Info, LayoutDashboard, LogIn, UserPlus, LogOut, FileText, CalendarCheck, CheckCircle, CreditCard, User, MessageCircle, Compass } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import '../styles/MobileMenuNew.css';
import { playSparkSound, playWhoosh } from '../utils/sparkSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_OPTIONS = [
  { id: 'avatar1', bg: 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)' },
  { id: 'avatar2', bg: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)' },
  { id: 'avatar3', bg: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)' },
  { id: 'avatar4', bg: 'linear-gradient(135deg, #A855F7 0%, #6366F1 100%)' },
  { id: 'avatar5', bg: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)' },
  { id: 'avatar6', bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
];

const getFileUrl = (fileRef) => {
  if (!fileRef) return '';
  if (fileRef.startsWith('http://') || fileRef.startsWith('https://')) return fileRef;
  return `${BACKEND_URL}/api/uploads/${fileRef}`;
};

const MobileMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();

  const getAvatarStyle = () => {
    if (user?.photo) {
      return { backgroundImage: `url(${getFileUrl(user.photo)})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    if (user?.avatar) {
      const found = AVATAR_OPTIONS.find(a => a.id === user.avatar);
      if (found) return { background: found.bg };
    }
    return { background: 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)' };
  };

  // Track previous open state so we can play the whoosh only on true→false transitions
  const wasOpenRef = useRef(false);

  // Lock body scroll AND preserve scroll position so the page doesn't
  // jump when menu opens/closes. Also plays the spark sound on open.
  useEffect(() => {
    if (isOpen) {
      // Play spark/zap synth on open — in sync with the shockwave visual
      try { playSparkSound(); } catch {}
      wasOpenRef.current = true;
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.dataset.mmScroll = String(scrollY);
      document.body.classList.add('mm-menu-active');
    } else {
      // Only play whoosh on actual open→close transitions
      // (not on initial mount when isOpen starts false).
      if (wasOpenRef.current) {
        try { playWhoosh(); } catch {}
        wasOpenRef.current = false;
      }
      const saved = document.body.dataset.mmScroll;
      document.body.classList.remove('mm-menu-active');
      document.body.style.top = '';
      if (saved) {
        const y = parseInt(saved, 10) || 0;
        // Restore scroll position without animation
        window.scrollTo(0, y);
        delete document.body.dataset.mmScroll;
      }
    }
    return () => {
      document.body.classList.remove('mm-menu-active');
      document.body.style.top = '';
    };
  }, [isOpen]);

  const go = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    go('/');
  };

  const isActive = (path) => location.pathname === path;

  // Render menu via Portal so it lives outside #root and isn't affected by the transform
  return ReactDOM.createPortal(
    <>
      {isOpen && <div className="mm-overlay mm-overlay-visible" onClick={onClose} data-testid="mobile-menu-overlay" />}
      <div className={`mm-panel ${isOpen ? 'mm-open' : ''}`} data-testid="mobile-menu-panel">
        {/* Shockwave ring — pulses out when menu opens for a cinematic feel */}
        <span className="mm-shockwave" aria-hidden="true"></span>
        {/* Brand Header */}
        <div className="mm-brand" data-testid="mobile-menu-brand">
          {isAuthenticated && user ? (
            <>
              <div className="mm-user-avatar" style={getAvatarStyle()} data-testid="mobile-menu-user-avatar">
                {!user.photo && !user.avatar && (
                  <span className="mm-avatar-initial">{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                )}
              </div>
              <span className="mm-brand-name" data-testid="mobile-menu-user-name">{user.name || 'User'}</span>
            </>
          ) : (
            <>
              <div className="mm-brand-icon">R</div>
              <span className="mm-brand-name">Registan Tourism</span>
            </>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="mm-nav" data-testid="mobile-menu-nav">
          <button
            className={`mm-item ${isActive('/') ? 'mm-active' : ''}`}
            onClick={() => go('/')}
            data-testid="mobile-menu-home"
          >
            <Home size={20} strokeWidth={1.8} />
            <span>{t('home') || 'Home'}</span>
          </button>

          <button
            className={`mm-item ${isActive('/start-business') ? 'mm-active' : ''}`}
            onClick={() => go('/start-business')}
            data-testid="mobile-menu-business"
          >
            <Briefcase size={20} strokeWidth={1.8} />
            <span>{t('startBusiness') || 'Start Business'}</span>
          </button>

          <button
            className={`mm-item ${isActive('/tours') ? 'mm-active' : ''}`}
            onClick={() => go('/tours')}
            data-testid="mobile-menu-tours"
          >
            <Compass size={20} strokeWidth={1.8} />
            <span>{t('tours') || 'Tours'}</span>
          </button>

          <button
            className={`mm-item ${isActive('/about') ? 'mm-active' : ''}`}
            onClick={() => go('/about')}
            data-testid="mobile-menu-about"
          >
            <Info size={20} strokeWidth={1.8} />
            <span>{t('about') || 'About'}</span>
          </button>

          <button
            className={`mm-item ${isActive('/tourismsoftware') ? 'mm-active' : ''}`}
            onClick={() => go('/tourismsoftware')}
            data-testid="mobile-menu-tourism-software"
          >
            <FileText size={20} strokeWidth={1.8} />
            <span>Tourism Software</span>
          </button>

          {/* Section Divider */}
          <div className="mm-section-divider">
            <span>{t('account') || 'Account'}</span>
          </div>

          {isAuthenticated && user ? (
            <>
              <button
                className={`mm-item ${isActive('/dashboard') ? 'mm-active' : ''}`}
                onClick={() => go('/dashboard')}
                data-testid="mobile-menu-dashboard"
              >
                <LayoutDashboard size={20} strokeWidth={1.8} />
                <span>{t('dashboard') || 'Dashboard'}</span>
              </button>

              <button
                className="mm-item"
                onClick={() => go('/dashboard?tab=bookings')}
                data-testid="mobile-menu-my-bookings"
              >
                <CalendarCheck size={20} strokeWidth={1.8} />
                <span>{t('myBookings') || 'My Bookings'}</span>
              </button>

              <button
                className="mm-item"
                onClick={() => go('/dashboard?tab=approved')}
                data-testid="mobile-menu-approved"
              >
                <CheckCircle size={20} strokeWidth={1.8} />
                <span>{t('approved') || 'Approved'}</span>
              </button>

              <button
                className="mm-item"
                onClick={() => go('/dashboard?tab=transactions')}
                data-testid="mobile-menu-transactions"
              >
                <CreditCard size={20} strokeWidth={1.8} />
                <span>{t('transactions') || 'Transactions'}</span>
              </button>

              <button
                className="mm-item mm-logout"
                onClick={handleLogout}
                data-testid="mobile-menu-logout"
              >
                <LogOut size={20} strokeWidth={1.8} />
                <span>{t('logout') || 'Logout'}</span>
              </button>
            </>
          ) : (
            <>
              <button
                className="mm-item"
                onClick={() => go('/login')}
                data-testid="mobile-menu-login"
              >
                <LogIn size={20} strokeWidth={1.8} />
                <span>{t('login') || 'Login'}</span>
              </button>

              <button
                className="mm-item"
                onClick={() => go('/register')}
                data-testid="mobile-menu-register"
              >
                <UserPlus size={20} strokeWidth={1.8} />
                <span>{t('signUp') || 'Sign Up'}</span>
              </button>
            </>
          )}
        </nav>

        {/* Chat Button */}
        <button
          className="mm-chat-btn"
          onClick={() => {
            onClose();
            setTimeout(() => window.dispatchEvent(new Event('toggle-chat')), 300);
          }}
          data-testid="mobile-menu-chat"
        >
          <MessageCircle size={20} strokeWidth={1.8} />
          <span>{t('support') || 'Support'}</span>
        </button>

        {/* Footer Branding */}
        <div className="mm-footer" data-testid="mobile-menu-footer">
          <div className="mm-footer-brand">Registan Tourism</div>
          <div className="mm-footer-sub">UAE Visa & Business Services</div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MobileMenu;
